import { twins } from '@/api/buildingTwin';
import { selectHomeTwinBuilding, homeTwinApartmentSelection, validHomeTwinFloor } from '@/utils/homeTwinSelection';

export const emptyHomeTwinSnapshot = () => ({ heatAreas: [], rooms: [], devices: [] });
export function createHomeTwinState() {
  return { userId: null, projectId: null, scopeGeneration: 0, selectionGeneration: 0, catalogGeneration: 0, buildings: [], conflicts: [],
    buildingId: null, expanded: false, document: null, selectedApartmentRef: null, selectedFloorRef: null, snapshot: emptyHomeTwinSnapshot(),
    loadingCatalog: false, loadingDetail: false, loadingSnapshot: false, error: '', catalogError: '' };
}
const message = error => error?.body?.message || error?.message || 'Gebäudemodell konnte nicht geladen werden.';
const tokenFor = state => ({ scope: state.scopeGeneration, selection: state.selectionGeneration, modelId: state.buildings.find(item => item.buildingId === state.buildingId)?.modelId });
const valid = (state, token) => state.expanded && state.scopeGeneration === token.scope && state.selectionGeneration === token.selection && state.buildings.find(item => item.buildingId === state.buildingId)?.modelId === token.modelId;
const staleSnapshot = snapshot => ({ ...snapshot, stale: true, rooms: (snapshot.rooms || []).map(room => ({ ...room,
  actual: room.actual ? { ...room.actual, quality: 'stale' } : room.actual })), heatAreas: (snapshot.heatAreas || []).map(area => ({ ...area,
  actual: area.actual ? { ...area.actual, quality: 'stale' } : area.actual,
  floorTemperatureEstimate: area.floorTemperatureEstimate ? { ...area.floorTemperatureEstimate, quality: 'stale' } : area.floorTemperatureEstimate,
  thermalPower: area.thermalPower ? { ...area.thermalPower, quality: 'stale' } : area.thermalPower,
  estimatedMean: area.estimatedMean ? { ...area.estimatedMean, quality: 'stale' } : area.estimatedMean })) });

export function createHomeTwinModule(client = twins) {
  // A stopped poller cannot cancel its HTTP request. Serialize snapshots across
  // reopen/scope changes, while generation guards discard every obsolete reply.
  let pendingSnapshot = null;
  return {
    namespaced: true, state: createHomeTwinState,
    getters: { selectedBuilding: state => state.buildings.find(item => item.buildingId === state.buildingId) || null },
    mutations: {
      SET_SCOPE(state, { userId, projectId }) {
        const scopeGeneration = state.scopeGeneration + 1, selectionGeneration = state.selectionGeneration + 1;
        Object.assign(state, createHomeTwinState(), { userId, projectId, scopeGeneration, selectionGeneration });
      },
      SET_CATALOG(state, { buildings, conflicts, preferredId }) {
        const previous = state.buildings.find(item => item.buildingId === state.buildingId);
        state.buildings = buildings; state.conflicts = conflicts;
        const selected = selectHomeTwinBuilding(buildings, preferredId || state.buildingId);
        if (state.buildingId !== selected?.buildingId || previous?.modelId !== selected?.modelId) {
          state.selectionGeneration += 1; state.document = null; state.snapshot = emptyHomeTwinSnapshot();
          state.selectedApartmentRef = null; state.selectedFloorRef = null;
          state.loadingDetail = false; state.loadingSnapshot = false;
        }
        state.buildingId = selected?.buildingId || null;
      },
      SELECT_BUILDING(state, id) {
        if (state.buildingId === id) return;
        state.selectionGeneration += 1; state.buildingId = id; state.document = null;
        state.selectedApartmentRef = null; state.selectedFloorRef = null;
        state.snapshot = emptyHomeTwinSnapshot(); state.error = ''; state.loadingDetail = false; state.loadingSnapshot = false;
      },
      SET_EXPANDED(state, value) {
        if (state.expanded === value) return;
        state.expanded = value; state.selectionGeneration += 1;
        state.loadingDetail = false; state.loadingSnapshot = false; state.error = '';
        if (!value) { state.document = null; state.snapshot = emptyHomeTwinSnapshot(); state.selectedApartmentRef = null; state.selectedFloorRef = null; }
      },
      INVALIDATE_SELECTION(state) {
        state.selectionGeneration += 1; state.document = null; state.snapshot = emptyHomeTwinSnapshot();
        state.selectedApartmentRef = null; state.selectedFloorRef = null;
        state.loadingDetail = false; state.loadingSnapshot = false; state.error = '';
      },
      PATCH(state, patch) { Object.assign(state, patch); },
      SELECT_APARTMENT(state, payload) {
        const selected = homeTwinApartmentSelection(state.document, payload);
        if (!state.expanded || !selected) return;
        state.selectedApartmentRef = selected.apartmentRef; state.selectedFloorRef = selected.floorRef;
      },
      SELECT_FLOOR(state, ref) { if (state.expanded && validHomeTwinFloor(state.document, ref)) { state.selectedFloorRef = ref; state.selectedApartmentRef = null; } },
      SHOW_BUILDING(state) { state.selectedApartmentRef = null; state.selectedFloorRef = null; },
    },
    actions: {
      async initialize({ commit, dispatch }, scope) {
        commit('SET_SCOPE', scope);
        commit('SET_EXPANDED', scope.expanded === true);
        await dispatch('loadCatalog', { preferredId: scope.buildingId });
      },
      async loadCatalog({ state, commit }, { preferredId } = {}) {
        if (!state.userId || !state.projectId) return;
        const generation = state.scopeGeneration, catalogGeneration = state.catalogGeneration + 1, projectId = state.projectId;
        const current = () => generation === state.scopeGeneration && catalogGeneration === state.catalogGeneration;
        commit('PATCH', { loadingCatalog: true, catalogError: '', catalogGeneration });
        try {
          const catalog = await client.home(projectId);
          if (!current()) return;
          if (String(catalog.projectId) !== String(projectId)) throw new Error('Projektzuordnung des Gebäudekatalogs stimmt nicht überein.');
          commit('SET_CATALOG', { buildings: catalog.buildings || [], conflicts: catalog.conflicts || [], preferredId });
        } catch (error) {
          if (current()) commit('PATCH', { catalogError: message(error) });
        } finally {
          if (current()) commit('PATCH', { loadingCatalog: false });
        }
      },
      async loadSelected({ state, getters, commit, dispatch }) {
        const building = getters.selectedBuilding;
        if (!state.expanded || !building || state.loadingDetail) return;
        const token = tokenFor(state), context = { projectId: state.projectId, buildingId: building.buildingId, revision: building.revision };
        commit('PATCH', { loadingDetail: true, error: '' });
        try {
          const document = await client.get(building.modelId, context);
          if (!valid(state, token)) return;
          if (String(document._id) !== String(building.modelId) || document.status !== 'active' || document.revision !== building.revision) throw new Error('Das Modell ist nicht mehr die aktive freigegebene Revision.');
          commit('PATCH', { document });
          await dispatch('poll');
        } catch (error) {
          if (!valid(state, token)) return;
          commit('PATCH', { error: message(error), document: null, snapshot: emptyHomeTwinSnapshot() });
          if ([403, 404, 409].includes(error.status)) await dispatch('loadCatalog');
        } finally {
          if (valid(state, token)) commit('PATCH', { loadingDetail: false });
        }
      },
      async poll({ state, getters, commit, dispatch }) {
        const token = tokenFor(state), building = getters.selectedBuilding;
        if (!building || !state.document || !valid(state, token) || state.loadingSnapshot) return;
        commit('PATCH', { loadingSnapshot: true });
        const snapshotAge = Date.now() - new Date(state.snapshot.timestamp).getTime();
        if (snapshotAge > (state.snapshot.freshnessSeconds || 300) * 1000 || snapshotAge < 0) commit('PATCH', { snapshot: staleSnapshot(state.snapshot) });
        try {
          while (pendingSnapshot) { try { await pendingSnapshot; } catch (_) { /* previous generation handles its error */ } }
          if (!valid(state, token)) return;
          const request = client.snapshot(building.modelId, { projectId: state.projectId, buildingId: building.buildingId, revision: building.revision });
          pendingSnapshot = request;
          let snapshot;
          try { snapshot = await request; } finally { if (pendingSnapshot === request) pendingSnapshot = null; }
          if (!valid(state, token)) return;
          if (String(snapshot.modelId) !== String(building.modelId) || String(snapshot.projectId) !== String(state.projectId) || String(snapshot.buildingId) !== String(building.buildingId) || snapshot.revision !== building.revision) throw new Error('Die Live-Daten gehören nicht zum ausgewählten Gebäudemodell.');
          commit('PATCH', { snapshot, error: '' });
        } catch (error) {
          if (!valid(state, token)) return;
          commit('PATCH', { error: message(error), snapshot: staleSnapshot(state.snapshot) });
          if ([403, 404, 409].includes(error.status)) {
            commit('PATCH', { document: null, snapshot: emptyHomeTwinSnapshot() });
            await dispatch('loadCatalog');
          }
        } finally {
          if (valid(state, token)) commit('PATCH', { loadingSnapshot: false });
        }
      },
    },
  };
}
export default createHomeTwinModule();
